import {ISelectItem} from '../../shared/types';

export interface IUser {
  firstName: string;
  lastName: string;
  emailID: string;
  roleName: string;
  status: string;
  securityGroupName: string;
  projectLabel: string;
  entityLabel: string;
  projectID: number;
  userId: number;
  entityID: number;
  isBillable: boolean;
  isSLHUserInOtherDomain: boolean;
  role: string;
}

export interface IGetUsersRequestPayload {
  filterRoleId: number;
  filterUserName?: string;
  sortBy?: string;
  sortingOrder?: string;
  pageNumber?: number;
  pageSize?: number;
  createdOn?: string;
  isActive?: boolean;
}

export interface NotLoggedInUser {
  firstName: string;
  lastName: string;
  emailID: string;
  projectLabel: string;
  emaillinkUrl: string;
  userProjectLinkID: number;
  domainLabel: string;
}
export interface IGetUsersResponse {
  data: IUser[];
  totalRecords: number;
}

export interface Project {
  key: number;
  value: string;
  isSelected?: boolean;
  text?: string | null;
}

export interface Domain {
  key: number;
  value: string;
  isSelected?: boolean;
  text?: string | null;
}

export interface IRole {
  key: number;
  value: string;
  isSelected: boolean;
  text: string | null;
}

export interface IGetRolesResponse {
  currentRole: string;
  roleId: number;
  entityId: number;
  projectId: number;
  securityGroupId: number;
  applicableRoles: IRole[];
}

interface RawProject {
  key: number;
  value: string;
  isSelected: boolean;
  text: string | null;
}

export interface GetProjectsResponse {
  projects: RawProject[];
}

export interface SecurityGroupItem {
  key: number;
  value: string;
  isSelected: boolean;
  text: string | null;
}

export interface BulkAccessListRequest {
  bulkRoleID: string;
  bulkProjectID: number;
  bulkSecurityGroupID: number;
}

export interface UserItem {
  userID: number;
  userName: string;
  roleName: string;
  roleID: number;
  projectName: string;
  selected?: boolean;
}

export interface UserRights {
  [key: string]: boolean;
}

export interface BulkAccessListResponse {
  users: UserItem[];
  userRights: UserRights;
  projectSetting?: null;
}

export interface IDeleteUserRequestPayload {
  userId: number;
  modifiedBy: number;
}

export interface IRemoveUserArgs {
  payload: string;
  projectId: number;
}
export interface IRemoveUserResponse {
  message: string;
}
export interface IGetNotLoggedInUsersPayload {
  filterRoleId: number;
  sortingdirection: string;
  displayLength: number;
  pageNumber: number;
  sortingColumn: string;
}

export interface IGetNotLoggedInUsersResponse {
  totalRecords: number;
  totalDisplayRecords: number;
  data: NotLoggedInUser[];
}

export interface IResendLinkRequestPayload {
  userProjectLinkId: string;
  projectLinkGUID: string;
  firstName: string;
  emailId: string;
  emailLinkUrl: string;
  domainLabel: string;
  projectLabel: string;
}

export interface IResendLinkResponse {
  success: boolean;
}

export interface IGetMetaResponse {
  projectName: string | null;
  projectId: number | null;
  groupId: number;
  roleId: number;
  isBillable: boolean;
  domainName: string | null;

  projectListSelect: ISelectItem[];
  projectList: Record<string, string>;

  roles: ISelectItem[];
  locale: Array<{
    key: number | string | null;
    value: string;
    isSelected: boolean;
    text: string;
  }>;

  securityGroups: [];

  domainListSelect: ISelectItem[];
  domainList: Record<string, string>;
}

export interface IAddUserPayload {
  firstName: string;
  lastName: string;
  emailId: string;
  localeCode: string;
  roleId: string;
  entityId: number;
  projectId: number;
  securityGroupId: string[];
  isBillable: boolean;
  isActive: boolean;
  ssoEnabled: boolean;
  recoveryEmailId: string;
  isAttorneyProfile: boolean;
}
export interface IAddUserSuccessResponse {
  message: string;
  userId: number;
}

export interface IBulkUserAccessResponse {
  currentUserRole: number;
  currentProject: string;
  currentSecurityGroup: string;
  bulkAccessRoles: {
    text: string;
    value: string;
    selected: boolean;
    encryptedID: string;
  }[];
  bulkAccessProjects: {
    key: number;
    value: string;
    isSelected: boolean;
    text: string | null;
  }[];
  bulkSecurityGroups: [];
  aiModelingEnabled: boolean;
}

export interface ISecurityGroupResponse {
  Value: string;
  Text: string;
  Disabled: boolean;
  Group?: string | null;
  Selected?: boolean;
}

export interface IAccessListResponse {
  users: IUser[];
  userRights: UserRights;
}

export interface IGetBulkUserRightsPayload {
  userId: number;
  roleId: number;
  projectId: number;
  securityGroupId: number;
  canManage: boolean;
  canIngestions: boolean;
  canProductions: boolean;
  canPredictiveCoding: boolean;
  canSearching: boolean;
  canReceiveTeamCenterMessages: boolean;
  canConceptExplorer: boolean;
  canCommunicationsExplorer: boolean;
  canAnalyticsPanels: boolean;
  canEditCustomMetadata: boolean;
  canViewFullDocList: boolean;
  canAllReports: boolean;
  canReportAdministration: boolean;
  canDownloadNative: boolean;
  canRedactions: boolean;
  canHighlighting: boolean;
  canReviewerRemarks: boolean;
  canUndockPanels: boolean;
  accessRight: boolean;
  selectedUserID: number[];
  canAIModels: boolean;
}

export interface IBulkAccessSuccessResponse {
  success: boolean;
  message: string;
}

export interface IUserRights {
  canManage: boolean | null;
  canIngestions: boolean | null;
  canProductions: boolean | null;
  canPredictiveCoding: boolean | null;
  canSearching: boolean | null;
  canReceiveTeamCenterMessages: boolean | null;
  canConceptExplorer: boolean | null;
  canCommunicationsExplorer: boolean | null;
  canAnalyticsPanels: boolean | null;
  canEditCustomMetadata: boolean | null;
  canViewFullDocList: boolean | null;
  canAllReports: boolean | null;
  canReportAdministration: boolean | null;
  canDownloadNative: boolean | null;
  canRedactions: boolean | null;
  canHighlighting: boolean | null;
  canReviewerRemarks: boolean | null;
  canUndockPanels: boolean | null;
  canAIModels: boolean | null;
}

export const emptyUserRights: IUserRights = {
  canManage: null,
  canIngestions: null,
  canProductions: null,
  canPredictiveCoding: null,
  canSearching: null,
  canReceiveTeamCenterMessages: null,
  canConceptExplorer: null,
  canCommunicationsExplorer: null,
  canAnalyticsPanels: null,
  canEditCustomMetadata: null,
  canViewFullDocList: null,
  canAllReports: null,
  canReportAdministration: null,
  canDownloadNative: null,
  canRedactions: null,
  canHighlighting: null,
  canReviewerRemarks: null,
  canUndockPanels: null,
  canAIModels: null,
};

export const PERMISSIONS_CONFIG: IPermissionItem[] = [
  {label: 'Manage', key: 'canManage', section: 'Rights'},
  {label: 'Ingestions', key: 'canIngestions', section: 'Rights'},
  {label: 'Productions', key: 'canProductions', section: 'Rights'},
  {label: 'Searching', key: 'canSearching', section: 'Rights'},
  {label: 'Concept Explorer', key: 'canConceptExplorer', section: 'Rights'},
  {
    label: 'Communications Explorer',
    key: 'canCommunicationsExplorer',
    section: 'Rights',
  },

  // Reports
  {label: 'All Reports', key: 'canAllReports', section: 'Reports'},

  // Doc View Controls
  {
    label: 'Download Native',
    key: 'canDownloadNative',
    section: 'Doc View Controls',
  },
  {label: 'Redactions', key: 'canRedactions', section: 'Doc View Controls'},
  {label: 'Highlighting', key: 'canHighlighting', section: 'Doc View Controls'},
  {
    label: 'Reviewer Remarks',
    key: 'canReviewerRemarks',
    section: 'Doc View Controls',
  },
  {
    label: 'Analytics Panels',
    key: 'canAnalyticsPanels',
    section: 'Doc View Controls',
  },
];
export interface IPermissionItem {
  label: string;
  key: keyof IUserRights;
  section: 'Rights' | 'Reports' | 'Doc View Controls';
}

export interface IUserRightsResponse {
  userRights: IUserRights;
}

export interface IGetBulkUserRightsPayload {
  bulkRoleID: number;
  bulkProjectID?: number;
  bulkSecurityGroupID?: number;
}

export interface IBulkUser {
  userID: number;
  userName: string;
  roleName: string;
  roleID: number;
  projectName: string;
}

export interface IProjectSetting {
  projectID: number;
  isAIEnabled: boolean;
}

export interface IBulkUserAccessResponse {
  usersList: IBulkUser[];
  userRights: IUserRights;
  projectSetting: IProjectSetting;
}

export interface ApplicableRole {
  key: number;
  value: string;
  isSelected: boolean;
  text: string | null;
}

export interface UserRoleResponse {
  currentRole: string | null;
  roleId: number;
  entityId: number;
  projectId: number;
  securityGroupId: number;
  applicableRoles: ApplicableRole[];
}

export interface IChangeUserRolePayload {
  role: number;
  entityID?: number;
  projectID?: number;
  securityGroupID?: number;
}

export interface IChangeUserRoleResponse {
  userId: number;
  baseRoleId: number;
  roleId: number;
  roleName: string;
  entityId: number;
  projectId: number;
  securityGroupId: number;
  landingPage: string;
  userName: string | null;
  emailID: string | null;
}

export interface IUserDetailsResponse {
  oldSecurityGroups?: string | SecurityGroupItem[] | null;
  userContextEntity?: number;
  domainEntityList?: IDomainOrSecurityGroupItem[] | null;
  isDomainEntityProject?: boolean;
  selectedDomain?: string | null;
  projects?: IProjectItem[] | null;
  selectedProject?: string | IProjectItem | null;
  userRole?: string | null;
  userLocale?: string | null;
  userSG?: string | null;
  role: number;
  userPj?: number;
  selectedSecGroup?: SecurityGroupItem | null;
  locked?: boolean;
  securityGroups?: SecurityGroupItem[];
  groupID?: number;
  locale?: ILocaleItem[];
  roles?: IRoleItem[];
  isBillable?: boolean;
  aiModelingEnabled?: boolean;
  genAiEnabled?: boolean;
  projectID?: number;
  roleName?: string | null;
  isActive?: boolean;
  isDeleted?: boolean;
  status?: string | null;
  createdBy?: number;
  modifiedBy?: number;
  localeCode?: string;
  firstName?: string;
  lastName?: string;
  emailID?: string;
  recoveryEmailID?: string;
  createdOn?: string;
  modifiedOn?: string;
  securityGroupID?: number;
  securityGroupName?: string;
  rowNumber?: number;
  projectLabel?: string;
  isLocked?: boolean;
  userRights: Record<string, boolean | 'disabled'>;
  securityGroupIDs?: (string | number)[];
  newSecurityGroupIDs?: string | number[] | null;
  newSecurityGroupNames?: string | null;
  entityLabel?: string;
  entityID?: number;
  previousEntityID?: number;
  previousProjectID?: number;
  previousRoleID?: string | number;
  isAttorneyProfile?: boolean;
  ssoEnabled?: boolean;
  userId?: number;
  isEditProfile?: boolean;
  aiBatchSummarize?: boolean;
  aiInvestgate?: boolean;
  aiReview?: boolean;
}

export interface IProjectItem {
  key: number | string;
  value: string;
  isSelected: boolean;
  text: string | null;
}

export interface IRoleItem {
  key: number | string;
  value: string;
  isSelected: boolean;
  text: string | null;
}

export interface ILocaleItem {
  localeCode: string;
  localeLabel: string;
}

export interface IDomainOrSecurityGroupItem {
  key: number | string;
  value: string;
  isSelected?: boolean;
  text?: string | null;
}

export interface IEditUserRequestPayload {
  firstName: string;
  lastName: string;
  emailId: string;
  localeCode: string;
  roleId: string | undefined;
  entityId: number | undefined;
  projectId: string | number | true | undefined;
  securityGroupId: number | undefined;
  securityGroupIds: string[];
  isBillable: boolean;
  isActive: boolean;
  isLocked: boolean;
  ssoEnabled: boolean | undefined;
  isAttorneyProfile: boolean;
  previousRoleID: string | undefined;
  previousProjectID: number | undefined;
  previousEntityID: number | undefined;
  userRights: Record<string, boolean | 'disabled'>;
  previousFirstName: string | undefined;
  previousLastName: string | undefined;
  previousIsActive: boolean | undefined;
  previousIsBillable: boolean | undefined;
}

export interface IEditUserSuccessResponse {
  message: string;
  userId: number;
}

export interface UseDomainsOptions {
  enabled?: boolean;
}
